import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { User, Building2, Link2, Save, Loader2, RefreshCw, ExternalLink, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [crmConnections, setCrmConnections] = useState([]);
  const [crmForm, setCrmForm] = useState({
    crm_type: 'dynamics',
    instance_url: '',
    tenant_id: '',
    client_id: '',
    client_secret: ''
  });

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const res = await api().get('/crm/connections');
        setCrmConnections(res.data.connections || []);
      } catch (err) {
        console.error('Failed to fetch CRM connections:', err);
      }
    };
    fetchConnections();
  }, [api]);

  const handleCrmConnect = async () => {
    if (crmForm.crm_type === 'dynamics' && !crmForm.tenant_id) {
      toast.error('Please enter the Azure Tenant ID');
      return;
    }
    if (crmForm.crm_type === 'salesforce' && !crmForm.instance_url) {
      toast.error('Please enter the Salesforce instance URL');
      return;
    }
    if (crmForm.crm_type === 'dubsado' && !crmForm.client_id) {
      toast.error('Please enter your Dubsado Zapier API Key');
      return;
    }
    setLoading(true);
    try {
      await api().post('/crm/connect', crmForm);
      const crmName = crmForm.crm_type === 'dynamics' ? 'Microsoft Dynamics' : 
                      crmForm.crm_type === 'dubsado' ? 'Dubsado' : 'Salesforce';
      toast.success(`${crmName} connection configured!`);
      const res = await api().get('/crm/connections');
      setCrmConnections(res.data.connections || []);
      setCrmForm({ crm_type: 'dynamics', instance_url: '', tenant_id: '', client_id: '', client_secret: '' });
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 'Failed to connect CRM';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSync = async (connectionId, crmType) => {
    setSyncing(connectionId);
    try {
      const res = await api().post(`/crm/sync/${connectionId}`);
      const crmName = crmType === 'dynamics' ? 'Microsoft Dynamics' : 
                      crmType === 'dubsado' ? 'Dubsado' : 'Salesforce';
      toast.success(`${crmName} sync initiated! ${res.data.estimated_records} records queued.`);
    } catch (err) {
      toast.error('Failed to start sync');
    } finally {
      setSyncing(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-zinc-400">Manage your account and integrations</p>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="bg-zinc-800/50 p-1 rounded-lg">
            <TabsTrigger value="profile" className="data-[state=active]:bg-indigo-600">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="crm" className="data-[state=active]:bg-indigo-600">
              <Link2 className="w-4 h-4 mr-2" />
              CRM Integration
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-6">
            <div className="glass-card rounded-xl p-6 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-indigo-600/30 flex items-center justify-center">
                  <span className="text-2xl text-indigo-400 font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{user?.name}</h3>
                  <p className="text-zinc-400">{user?.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Full Name</Label>
                  <Input
                    value={user?.name || ''}
                    disabled
                    className="input-dark mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Email</Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="input-dark mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Company</Label>
                  <Input
                    value={user?.company || 'Not set'}
                    disabled
                    className="input-dark mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Account Type</Label>
                  <Input
                    value={user?.role || 'user'}
                    disabled
                    className="input-dark mt-1 capitalize"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-white/10">
                <h4 className="text-white font-medium mb-2">Subscription</h4>
                <div className="flex items-center gap-4">
                  <span className="badge-info capitalize text-base px-3 py-1">{user?.tier} Plan</span>
                  <p className="text-zinc-400 text-sm">
                    Access to {user?.datasets_access?.length || 0} datasets
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* CRM Tab */}
          <TabsContent value="crm" className="mt-6">
            <div className="glass-card rounded-xl p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">CRM Integration</h3>
                <p className="text-zinc-400 text-sm">
                  Connect to Microsoft Dynamics or Salesforce to sync your compliance data.
                  {user?.tier !== 'enterprise' && user?.tier !== 'pro' && (
                    <span className="text-amber-400 block mt-2">
                      Upgrade to Pro or Enterprise to enable CRM integration.
                    </span>
                  )}
                </p>
              </div>

              {/* Existing Connections */}
              {crmConnections.length > 0 && (
                <div className="mb-6 space-y-3">
                  <h4 className="text-white font-medium">Connected CRMs</h4>
                  {crmConnections.map((conn) => (
                    <div key={conn.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          conn.crm_type === 'dynamics' ? 'bg-blue-500/20' : 
                          conn.crm_type === 'dubsado' ? 'bg-purple-500/20' : 'bg-sky-500/20'
                        }`}>
                          {conn.crm_type === 'dynamics' ? (
                            <svg className="w-5 h-5 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                            </svg>
                          ) : conn.crm_type === 'dubsado' ? (
                            <span className="text-xl">💜</span>
                          ) : (
                            <Building2 className="w-5 h-5 text-sky-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {conn.crm_type === 'dynamics' ? 'Microsoft Dynamics 365' : 
                             conn.crm_type === 'dubsado' ? 'Dubsado' : 'Salesforce'}
                          </p>
                          <p className="text-xs text-zinc-500">
                            Connected {new Date(conn.created_at).toLocaleDateString()}
                            {conn.crm_type === 'dubsado' && ' via Zapier'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                          <CheckCircle className="w-3 h-3" />
                          {conn.status}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleSync(conn.id, conn.crm_type)}
                          disabled={syncing === conn.id}
                          className="text-indigo-400 hover:text-indigo-300"
                        >
                          {syncing === conn.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Sync
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* New Connection Form */}
              {(user?.tier === 'pro' || user?.tier === 'enterprise') && (
                <div className="space-y-4 border-t border-white/10 pt-6">
                  <h4 className="text-white font-medium">Add New Connection</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-300">CRM Type</Label>
                      <Select 
                        value={crmForm.crm_type} 
                        onValueChange={(v) => setCrmForm(prev => ({ ...prev, crm_type: v }))}
                      >
                        <SelectTrigger className="input-dark mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dynamics">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-blue-500" />
                              Microsoft Dynamics 365
                            </span>
                          </SelectItem>
                          <SelectItem value="salesforce">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-sky-500" />
                              Salesforce
                            </span>
                          </SelectItem>
                          <SelectItem value="dubsado">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-purple-500" />
                              Dubsado
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* CRM-specific fields */}
                    {crmForm.crm_type === 'dubsado' ? (
                      <>
                        <div>
                          <Label className="text-zinc-300">Zapier API Key</Label>
                          <Input
                            placeholder="Your Dubsado Zapier API token"
                            value={crmForm.client_id}
                            onChange={(e) => setCrmForm(prev => ({ ...prev, client_id: e.target.value }))}
                            className="input-dark mt-1"
                            data-testid="crm-zapier-key-input"
                          />
                          <p className="text-xs text-zinc-500 mt-1">Found in Dubsado → Settings → Integrations → Zapier</p>
                        </div>
                        <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                          <p className="text-sm text-purple-300 font-medium">Dubsado Integration</p>
                          <p className="text-xs text-zinc-400 mt-1">
                            Connect via Zapier to sync leads, projects, and payments. 
                            <span className="text-purple-400"> Webhook integration coming soon.</span>
                          </p>
                        </div>
                      </>
                    ) : crmForm.crm_type === 'dynamics' ? (
                      <>
                        <div>
                          <Label className="text-zinc-300">Azure Tenant ID</Label>
                          <Input
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            value={crmForm.tenant_id}
                            onChange={(e) => setCrmForm(prev => ({ ...prev, tenant_id: e.target.value }))}
                            className="input-dark mt-1"
                            data-testid="crm-tenant-input"
                          />
                          <p className="text-xs text-zinc-500 mt-1">Found in Azure Portal → Azure Active Directory</p>
                        </div>
                        <div>
                          <Label className="text-zinc-300">Application (Client) ID</Label>
                          <Input
                            placeholder="Azure App Registration Client ID"
                            value={crmForm.client_id}
                            onChange={(e) => setCrmForm(prev => ({ ...prev, client_id: e.target.value }))}
                            className="input-dark mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-300">Client Secret</Label>
                          <Input
                            type="password"
                            placeholder="OAuth Client Secret"
                            value={crmForm.client_secret}
                            onChange={(e) => setCrmForm(prev => ({ ...prev, client_secret: e.target.value }))}
                            className="input-dark mt-1"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <Label className="text-zinc-300">Instance URL</Label>
                          <Input
                            placeholder="https://your-instance.salesforce.com"
                            value={crmForm.instance_url}
                            onChange={(e) => setCrmForm(prev => ({ ...prev, instance_url: e.target.value }))}
                            className="input-dark mt-1"
                            data-testid="crm-instance-input"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-300">Client ID</Label>
                          <Input
                            placeholder="OAuth Client ID"
                            value={crmForm.client_id}
                            onChange={(e) => setCrmForm(prev => ({ ...prev, client_id: e.target.value }))}
                            className="input-dark mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-300">Client Secret</Label>
                          <Input
                            type="password"
                            placeholder="OAuth Client Secret"
                            value={crmForm.client_secret}
                            onChange={(e) => setCrmForm(prev => ({ ...prev, client_secret: e.target.value }))}
                            className="input-dark mt-1"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <Button
                    onClick={handleCrmConnect}
                    disabled={loading}
                    className="btn-primary"
                    data-testid="connect-crm-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Connect CRM
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
